<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representative" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium mb-1">
          {{ $t('representative.representative') }} - {{ representative.name }}
        </label>
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="remoteCode" class="text-sm font-medium">{{
          $t('transactions.remoteCode')
        }}</label>
        <BaseInput
          v-model="modifiedData.paymentMethodRemotedCode"
          color="base"
          :disabled="mode === 'assign' || viewOnly"
          show-error-as-tooltip
          :error="
            $v.paymentMethodRemotedCode.required.$invalid
              ? $v.paymentMethodRemotedCode.required.$message
              : $v.paymentMethodRemotedCode.uniqueRemoteCode.$invalid
                ? $v.paymentMethodRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="paymentType" class="text-sm font-medium">{{
          $t('representative.companyPaymentType')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropdownPaymentTypes"
          v-model="selectedPaymentType"
          :no-results-text="$t('messages.noResultsText')"
          name="paymentType"
          show-error-as-tooltip
          internal-filter
          :disabled="!modifiedData.isFromManager || viewOnly"
          :error="$v.paymentTypeId.required.$invalid ? $v.paymentTypeId.required.$message : ''"
          @item-selected="handlePaymentTypeSelection"
          @clear-selection="handlePaymentTypeSelection" />
      </div>

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        :disable="mode === 'assign'"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseToggle from '../common/BaseToggle.vue';

import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import type { DCPaymentType } from '@/types/DataCollection';
import type { Representative } from '@/types/Traders';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit' | 'assign' | 'view';
    paymentType: Partial<DCPaymentType>;
    representativeId: string;
    representative?: Partial<Representative>;
    existingPaymentTypes?: DCPaymentType[];
    viewOnly?: boolean;
  }>(),
  {}
);

const { getPaymentTypesLookup } = useLookupsStore();
const { paymentTypes: lookupPaymentTypes, dropdownPaymentTypes } = storeToRefs(useLookupsStore());

const modifiedData = ref<Partial<DCPaymentType>>(
  deepCopy<Partial<DCPaymentType>>(props.paymentType)
);

const isModified = ref<boolean>(false);

const validationModel = ref({
  paymentTypeId: modifiedData.value.paymentTypeId || '',
  paymentMethodRemotedCode: modifiedData.value.paymentMethodRemotedCode || '',
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingPaymentTypes) return true;

  const existingDuplicate = props.existingPaymentTypes.find(
    (paymentType) =>
      paymentType.id !== modifiedData.value.id &&
      paymentType.paymentMethodRemotedCode === value &&
      paymentType.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = computed(() => ({
  paymentTypeId: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  paymentMethodRemotedCode: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    uniqueRemoteCode: helpers.withMessage(
      $t('messages.errors.duplicateRemoteCode'),
      isUniqueRemoteCode
    ),
  },
}));

const $v = useVuelidate(rules, validationModel);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.paymentTypeId = newData.paymentTypeId || '';
    validationModel.value.paymentMethodRemotedCode = newData.paymentMethodRemotedCode || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.paymentType);
  },
  { deep: true }
);

const selectedPaymentType = ref<SelectDropdownItem | null>(null);

function onDismissChanges() {
  modifiedData.value = deepCopy<Partial<DCPaymentType>>(props.paymentType);
  emit('closeModal');
}

function onSave() {
  $v.value.$validate();
  if ($v.value.$invalid) return;
  modifiedData.value.representativeId = props.representativeId;
  if (modifiedData.value.recordMode !== RecordMode.Added)
    modifiedData.value.recordMode = props.mode === 'add' ? RecordMode.Added : RecordMode.Modified;
  emit('save', modifiedData.value);
}

function handlePaymentTypeSelection(item: SelectDropdownItem) {
  selectedPaymentType.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.paymentTypeId = item ? item.value.toString() : '';
    const paymentType = lookupPaymentTypes.value.find((x) => x.id == item.value.toString());
    if (paymentType) modifiedData.value.paymentType = paymentType;
  }
}

onMounted(async () => {
  await getPaymentTypesLookup();

  if (modifiedData.value && modifiedData.value.paymentTypeId) {
    const foundType = lookupPaymentTypes.value.find(
      (t) => t.id === modifiedData.value.paymentTypeId
    );
    if (foundType) {
      selectedPaymentType.value = {
        name: foundType.name as string,
        value: foundType.id,
      };
    }
  }
});

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.paymentType')}`;
}
</script>
